import { memo, useCallback, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';

import { LeagueNavigator, LeagueNavigatorItem } from '@/components/league-navigator/components/LeagueNavigator';
import { LeagueNavigatorSkeleton } from '@/components/league-navigator/components/LeagueNavigatorSkeleton';
import { SPECIAL_TABS } from '@/components/league-navigator/utils/league-navigator-utils';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import {
    TileType,
    lobbyId,
    promosId,
    useLobbySectionSelection,
} from '@/feature/lobby/hooks/use-lobby-section-selection';
import { useGetOrderedLeaguesByEvents } from '@/hooks/use-fantasy-league-configs';
import { useResumeEffect } from '@/hooks/use-resume';
import { PickemLobbyTabNavigationProp } from '@/navigation/pickem/types';
import { League } from '@/types/api.generated';

import { usePlayerFiltersActions } from '../hooks/use-player-filters';
import { toLeagueNavigatorItem } from '../utils/league-item-utils';

type PickemNavigatorItem = LeagueNavigatorItem & { id: TileType };

const EXPLORE_ITEM: PickemNavigatorItem = {
    id: lobbyId,
    label: 'Explore',
    testID: 'lobby-league-tile-Explore',
    ...SPECIAL_TABS.explore,
};
const PROMOS_ITEM: PickemNavigatorItem = {
    id: promosId,
    label: 'Promos',
    testID: 'lobby-league-tile-Promos',
    ...SPECIAL_TABS.promos,
};

export const PickemNavigationLaunchpad = memo(() => {
    const requestPolicy = useGQLRequestPolicy('cache-and-network');
    const { fetching, isSorting, refetch, sortedLeagues } = useGetOrderedLeaguesByEvents(false, requestPolicy);
    const { setParams, navigate } = useNavigation<PickemLobbyTabNavigationProp>();
    const { resetAllFilters } = usePlayerFiltersActions();
    const selectedId = useLobbySectionSelection(state => state.selectedTile);
    const setSelectedTile = useLobbySectionSelection(state => state.actions.setSelectedTile);

    useResumeEffect(refetch);

    const leagueItems = useMemo<PickemNavigatorItem[]>(
        () => sortedLeagues.map(l => ({ ...toLeagueNavigatorItem(l), testID: `lobby-league-tile-${l.league.label}` })),
        [sortedLeagues]
    );

    const items = useMemo<PickemNavigatorItem[]>(() => [EXPLORE_ITEM, PROMOS_ITEM, ...leagueItems], [leagueItems]);

    const onSelectLeague = useCallback(
        (item: PickemNavigatorItem) => {
            if (item.id === promosId) {
                BetrAnalytics.trackProductEvent(AnalyticsEvent.VIEW_ALL_PROMOS_FROM_LOBBY_TILE);
                navigate('LobbyPromohub');
                return;
            }

            setParams({ league: item.id as League });
            setSelectedTile(item.id);
            resetAllFilters();
        },
        [navigate, setParams, setSelectedTile, resetAllFilters]
    );

    const onViewAllPress = useCallback(() => navigate('PickemAllLeaguesScreen'), [navigate]);

    if (fetching || isSorting) {
        return <LeagueNavigatorSkeleton />;
    }

    return (
        <LeagueNavigator
            leagues={items}
            selectedId={selectedId}
            onSelectLeague={onSelectLeague}
            onViewAllPress={onViewAllPress}
            testID={'lobby-leagues-tab-bar'}
        />
    );
});
